/**
 * Floating round "reopen consent" button, shown once a consent decision exists
 * and the banner is hidden. Lives in its own shadow-DOM host (like the footer)
 * so it survives independently of the banner host.
 */

export interface ReopenButtonOptions {
	colors: {
		primary: string;
		background: string;
		/** Empty string = fallback rgba(15,23,42,.12), same as the banner. */
		border: string;
	};
	position: "left" | "right";
	language: "ro" | "en";
	/** Custom brand logo (same source as the banner header). Null = inline mark. */
	logo: string | null;
	logoAlt: string | null;
	onClick(): void;
}

const ARIA_LABELS: Record<"ro" | "en", string> = {
	ro: "Setări cookie-uri",
	en: "Cookie settings",
};

/**
 * The mark is injected via innerHTML, so the interpolated color must never be
 * able to break out of the attribute context. The server validates colors as
 * hex, but cached/inline-fallback configs carry no such guarantee — anything
 * that isn't a plain hex color falls back to the default brand indigo.
 */
function safeColor(color: string): string {
	return /^#[0-9a-fA-F]{3,8}$/.test(color) ? color : "#4f46e5";
}

/** Original inline cookie mark: bitten disc + chocolate-chip dots. */
function cookieMarkSvg(color: string): string {
	return `
		<svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true"
			stroke="${color}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
			<path d="M21.2 12.6A9.4 9.4 0 1 1 11.4 2.8a3.6 3.6 0 0 0 4 4.4 3.6 3.6 0 0 0 5.8 5.4Z" />
			<circle cx="8.6" cy="9.4" r="0.6" fill="${color}" stroke="none" />
			<circle cx="14.2" cy="14.8" r="0.6" fill="${color}" stroke="none" />
			<circle cx="9.4" cy="15.4" r="0.6" fill="${color}" stroke="none" />
			<circle cx="12.4" cy="11" r="0.6" fill="${color}" stroke="none" />
		</svg>`;
}

export class ReopenButton {
	private host: HTMLElement;
	private shadow: ShadowRoot;
	private button: HTMLButtonElement;
	private mounted = false;

	constructor(options: ReopenButtonOptions) {
		this.host = document.createElement("div");
		this.host.id = "fewcookies-reopen";
		this.host.style.cssText = "position:fixed;width:0;height:0;z-index:2147483646;";
		// Open shadow root: same style isolation as the banner, but inspectable
		// by tests (mirrors footer.ts).
		this.shadow = this.host.attachShadow({ mode: "open" });

		const border = options.colors.border || "rgba(15, 23, 42, 0.12)";

		const style = document.createElement("style");
		style.textContent = `
			:host { all: initial; }
			*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
			.cg-reopen {
				position: fixed;
				bottom: 20px;
				width: 52px;
				height: 52px;
				border-radius: 50%;
				display: flex;
				align-items: center;
				justify-content: center;
				background: ${options.colors.background};
				border: 1px solid ${border};
				box-shadow: 0 8px 24px rgba(15, 23, 42, 0.16);
				cursor: pointer;
				z-index: 2147483646;
				transition: transform 250ms ease-out, box-shadow 250ms ease-out;
			}
			.cg-reopen:hover {
				transform: scale(1.06);
				box-shadow: 0 12px 32px rgba(15, 23, 42, 0.2);
			}
			.cg-reopen:focus-visible {
				outline: 2px solid ${options.colors.primary};
				outline-offset: 2px;
			}
			.cg-reopen-left { left: 20px; }
			.cg-reopen-right { right: 20px; }
			.cg-reopen-logo {
				display: block;
				max-width: 32px;
				max-height: 32px;
				object-fit: contain;
			}
			.cg-hidden { display: none !important; }
			@media (prefers-reduced-motion: reduce) {
				.cg-reopen { transition: none; }
			}
		`;

		this.button = document.createElement("button");
		this.button.type = "button";
		this.button.className = `cg-reopen cg-reopen-${options.position} cg-hidden`;
		this.button.setAttribute("aria-label", ARIA_LABELS[options.language] ?? ARIA_LABELS.ro);
		// Mirror the banner header: show the custom brand logo when one is set,
		// otherwise fall back to the inline cookie mark. The logo src is assigned
		// as a DOM property (never interpolated into innerHTML) so the URL can't
		// break out into markup.
		if (options.logo) {
			const logo = document.createElement("img");
			logo.className = "cg-reopen-logo";
			logo.src = options.logo;
			logo.alt = options.logoAlt ?? "";
			this.button.appendChild(logo);
		} else {
			this.button.innerHTML = cookieMarkSvg(safeColor(options.colors.primary));
		}
		this.button.addEventListener("click", () => options.onClick());

		this.shadow.append(style, this.button);
	}

	mount(): void {
		if (this.mounted) return;
		this.mounted = true;
		document.body.appendChild(this.host);
	}

	show(): void {
		if (!this.mounted) this.mount();
		this.button.classList.remove("cg-hidden");
	}

	hide(): void {
		this.button.classList.add("cg-hidden");
	}

	destroy(): void {
		this.host.remove();
		this.mounted = false;
	}
}
